import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User as UserIcon, Shield, CheckCircle } from 'lucide-react';
import { useTheme } from '../../ThemeContext';
import { useAuth } from '../../utils/auth/AuthContext';
import { API_URL } from '../../types';

interface RoleWithPermissions {
  role_id: string;
  name: string;
  description?: string;
  permissions: {
    permission_id: string;
    resource_type: string;
    action: string;
    description?: string;
  }[];
}

interface UserProfile {
  user_id: string;
  email: string;
  username: string;
  first_name?: string;
  last_name?: string;
  is_active: boolean;
  is_superuser: boolean;
  sso_provider_id?: string;
  last_login?: string;
  created_at: string;
  updated_at: string;
  roles: RoleWithPermissions[];
}

const UserProfilePage: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/auth/me/permissions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load profile');
      }

      const data = await response.json();
      setProfile(data);
    } catch (err) {
      setError('Failed to load user profile');
      console.error('Failed to load profile:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 24, color: theme.text }}>
        Loading...
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ color: theme.text, marginBottom: 16 }}>
          {error || 'Profile not found'}
        </div>
        <button
          onClick={() => navigate('/')}
          style={{
            padding: '10px 20px',
            background: theme.buttonBackground,
            color: theme.buttonText,
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
            fontWeight: 500,
          }}
        >
          Back to Home
        </button>
      </div>
    );
  }

  // Collect all unique permissions across roles
  const allPermissions = new Map<string, { resource_type: string; action: string; description?: string; roles: string[] }>();
  
  profile.roles.forEach(role => {
    role.permissions.forEach(perm => {
      const key = `${perm.resource_type}:${perm.action}`;
      if (!allPermissions.has(key)) {
        allPermissions.set(key, {
          resource_type: perm.resource_type,
          action: perm.action,
          description: perm.description,
          roles: []
        });
      }
      allPermissions.get(key)!.roles.push(role.name);
    });
  });

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <button
          onClick={() => navigate('/')}
          style={{
            padding: 8,
            background: 'transparent',
            border: `1px solid ${theme.border}`,
            borderRadius: 4,
            cursor: 'pointer',
            color: theme.text,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <ArrowLeft size={18} />
        </button>
        <h1 style={{ color: theme.text, fontSize: 24, margin: 0 }}>
          My Profile & Permissions
        </h1>
      </div>

      {/* User Info Card */}
      <div style={{
        background: theme.cardBackground,
        border: `1px solid ${theme.border}`,
        borderRadius: 8,
        padding: 20,
        marginBottom: 24,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <UserIcon size={32} color={theme.accent} />
          <div>
            <h2 style={{ color: theme.text, fontSize: 20, margin: 0 }}>
              {profile.first_name && profile.last_name 
                ? `${profile.first_name} ${profile.last_name}` 
                : profile.username}
            </h2>
            <p style={{ color: theme.textSecondary, fontSize: 14, margin: 0 }}>
              {profile.email}
            </p>
          </div>
          {profile.is_superuser && (
            <span style={{
              marginLeft: 'auto',
              padding: '6px 12px',
              background: '#dcfce7',
              color: '#15803d',
              borderRadius: 4,
              fontSize: 12,
              fontWeight: 600,
            }}>
              SUPERUSER
            </span>
          )}
        </div>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 12,
          paddingTop: 16,
          borderTop: `1px solid ${theme.border}`
        }}>
          <div>
            <div style={{ color: theme.textSecondary, fontSize: 12, marginBottom: 4 }}>
              Username
            </div>
            <div style={{ color: theme.text, fontSize: 14, fontWeight: 500 }}>
              {profile.username}
            </div>
          </div>
          <div>
            <div style={{ color: theme.textSecondary, fontSize: 12, marginBottom: 4 }}>
              Status
            </div>
            <div style={{ color: profile.is_active ? '#16a34a' : '#dc2626', fontSize: 14, fontWeight: 500 }}>
              {profile.is_active ? 'Active' : 'Inactive'}
            </div>
          </div>
          {profile.last_login && (
            <div>
              <div style={{ color: theme.textSecondary, fontSize: 12, marginBottom: 4 }}>
                Last Login
              </div>
              <div style={{ color: theme.text, fontSize: 14, fontWeight: 500 }}>
                {new Date(profile.last_login).toLocaleString()}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Roles Section */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ color: theme.text, fontSize: 18, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Shield size={20} />
          My Roles ({profile.roles.length})
        </h2>
        
        {profile.roles.length === 0 ? (
          <div style={{
            background: theme.cardBackground,
            border: `1px solid ${theme.border}`,
            borderRadius: 8,
            padding: 20,
            color: theme.textSecondary,
            textAlign: 'center',
          }}>
            No roles assigned
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {profile.roles.map(role => (
              <div
                key={role.role_id}
                style={{
                  background: theme.cardBackground,
                  border: `1px solid ${theme.border}`,
                  borderRadius: 8,
                  padding: 16,
                }}
              >
                <div style={{ marginBottom: 12 }}>
                  <h3 style={{ color: theme.text, fontSize: 16, margin: 0, textTransform: 'uppercase' }}>
                    {role.name}
                  </h3>
                  {role.description && (
                    <p style={{ color: theme.textSecondary, fontSize: 14, margin: '4px 0 0 0' }}>
                      {role.description}
                    </p>
                  )}
                </div>
                
                {role.permissions.length > 0 && (
                  <div>
                    <div style={{ color: theme.textSecondary, fontSize: 12, marginBottom: 8, fontWeight: 600 }}>
                      PERMISSIONS ({role.permissions.length})
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {role.permissions.map(perm => (
                        <span
                          key={perm.permission_id}
                          style={{
                            padding: '4px 8px',
                            background: theme.background,
                            border: `1px solid ${theme.border}`,
                            borderRadius: 4,
                            fontSize: 12,
                            color: theme.text,
                          }}
                          title={perm.description}
                        >
                          {perm.resource_type}:{perm.action}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* All Permissions Section */}
      <div>
        <h2 style={{ color: theme.text, fontSize: 18, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <CheckCircle size={20} />
          All My Permissions ({allPermissions.size})
        </h2>
        
        {allPermissions.size === 0 ? (
          <div style={{
            background: theme.cardBackground,
            border: `1px solid ${theme.border}`,
            borderRadius: 8,
            padding: 20,
            color: theme.textSecondary,
            textAlign: 'center',
          }}>
            No permissions assigned
          </div>
        ) : (
          <div style={{
            background: theme.cardBackground,
            border: `1px solid ${theme.border}`,
            borderRadius: 8,
            padding: 16,
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                  <th style={{ color: theme.textSecondary, fontSize: 12, fontWeight: 600, textAlign: 'left', padding: '8px 12px' }}>
                    RESOURCE
                  </th>
                  <th style={{ color: theme.textSecondary, fontSize: 12, fontWeight: 600, textAlign: 'left', padding: '8px 12px' }}>
                    ACTION
                  </th>
                  <th style={{ color: theme.textSecondary, fontSize: 12, fontWeight: 600, textAlign: 'left', padding: '8px 12px' }}>
                    FROM ROLE(S)
                  </th>
                </tr>
              </thead>
              <tbody>
                {Array.from(allPermissions.values()).map((perm, index) => (
                  <tr 
                    key={`${perm.resource_type}:${perm.action}`}
                    style={{ 
                      borderBottom: index < allPermissions.size - 1 ? `1px solid ${theme.border}` : 'none'
                    }}
                  >
                    <td style={{ color: theme.text, fontSize: 14, padding: '12px' }}>
                      {perm.resource_type}
                    </td>
                    <td style={{ color: theme.text, fontSize: 14, padding: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        background: theme.background,
                        borderRadius: 4,
                        fontSize: 12,
                        fontWeight: 500,
                      }}>
                        {perm.action}
                      </span>
                    </td>
                    <td style={{ color: theme.textSecondary, fontSize: 13, padding: '12px' }}>
                      {perm.roles.join(', ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfilePage;
